import { Alert, Card, Col, Row, Segmented, Space, Statistic, Table, Tag, Typography } from "antd";
import { useCallback, useState } from "react";
import { api } from "../api";
import { useResource } from "../useResource";

interface TierTotals {
  orders: number;
  conversations: number;
  grossMinor: number;
  netMinor: number;
  currency?: string;
}

interface SkuCount {
  sku: string;
  count: number;
}

/** `SalesReportController` */
export interface SalesReport {
  funnel: {
    widgetOpenedManual: number;
    widgetOpenedProactive: number;
    conversationsStarted: number;
    conversationsWithRecommendation: number;
    conversationsWithAddToCart: number;
    addToCartRequested: number;
    addToCartSucceeded: number;
    addToCartFailed: number;
  };
  attribution: { direct: TierTotals; assisted: TierTotals; directConversionRate: number };
  handoff: {
    requested: number;
    accepted: number;
    timedOut: number;
    cancelled: number;
    averageWaitSeconds: number | null;
    p90WaitSeconds: number | null;
  };
  topRecommended: SkuCount[];
  topAddedToCart: SkuCount[];
}

interface AgentTotals {
  turns: number;
  p50DurationMs: number | null;
  p95DurationMs: number | null;
  failedRate: number;
  degradedRate: number;
  guardReplacedRate: number;
  llmTurns: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  cacheHitRate: number;
}

/** Model birleşimi başına aynı ölçüler (paket 4.6). */
interface AgentModelRow extends AgentTotals {
  models: string;
}

/** `AgentReportController` */
export interface AgentReport extends AgentTotals {
  results: Record<string, number>;
  byModels: AgentModelRow[];
}

interface UsageRow {
  kind: string;
  provider: string | null;
  unit: string;
  quantity: number;
  providerCostMicros: number | null;
}

/** `UsageReportController` */
export interface UsageReport {
  rows: UsageRow[];
  billableConversations: number;
  providerCostMicros: number;
  providerCostComplete: boolean;
  conversationCostMicros: number;
  costPerConversationMicros: number | null;
  conversationCostComplete: boolean;
  /** Bu ayın (UTC) bütçe durumu; bütçe tanımlı değilse `null`. */
  budget: {
    month: string;
    budgetMicros: number;
    spentMicros: number;
    ratio: number;
    state: "OK" | "WARNING" | "EXCEEDED";
  } | null;
}

interface Reports {
  sales: SalesReport;
  agent: AgentReport;
  usage: UsageReport;
}

const PERIODS = [
  { label: "Son 7 gün", value: 7 },
  { label: "Son 30 gün", value: 30 },
  { label: "Son 90 gün", value: 90 },
];

const KINDS: Record<string, string> = {
  llm_input_tokens: "LLM giriş tokenı",
  llm_output_tokens: "LLM çıkış tokenı",
  llm_cache_read_tokens: "LLM önbellekten okunan token",
  llm_cache_write_tokens: "LLM önbelleğe yazılan token",
  embedding_tokens: "Embedding tokenı",
  conversation: "Faturalanabilir konuşma",
};

export function money(minor: number, currency = "TRY"): string {
  return new Intl.NumberFormat("tr-TR", { style: "currency", currency }).format(minor / 100);
}

export function percent(rate: number): string {
  return `%${(rate * 100).toLocaleString("tr-TR", { maximumFractionDigits: 1 })}`;
}

function usd(micros: number): string {
  return new Intl.NumberFormat("tr-TR", { style: "currency", currency: "USD", maximumFractionDigits: 4 })
    .format(micros / 1_000_000);
}

function seconds(value: number | null): string {
  return value === null ? "—" : `${value} sn`;
}

/**
 * Mağaza raporları (v0.9 §13, paket 3.4). Dönem kayan penceredir (son N gün, şu ana kadar). Attribution tanımları:
 * doğrudan ve destekli ayrı gösterilir, destekli doğrudan rakamlara eklenmez; net ciroda iade düşülür, iptalin neti 0.
 */
export function ReportsPage() {
  const [days, setDays] = useState(30);
  const { data, error } = useResource(useCallback(async (): Promise<Reports> => {
    const to = new Date();
    const from = new Date(to.getTime() - days * 24 * 3600 * 1000);
    const q = `?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`;
    const [sales, agent, usage] = await Promise.all([
      api.get<SalesReport>(`/api/console/reports/sales${q}`),
      api.get<AgentReport>(`/api/console/reports/agent${q}`),
      api.get<UsageReport>(`/api/console/reports/usage${q}`),
    ]);
    return { sales, agent, usage };
  }, [days]));

  return (
    <Space orientation="vertical" size="large" style={{ width: "100%" }}>
      <Space style={{ justifyContent: "space-between", width: "100%" }}>
        <Typography.Title level={3} style={{ margin: 0 }}>Raporlar</Typography.Title>
        <Segmented options={PERIODS} value={days} onChange={(v) => setDays(Number(v))} aria-label="Dönem" />
      </Space>
      {error && <Alert type="error" title={error} showIcon />}
      {data && <ReportCards reports={data} />}
    </Space>
  );
}

function ReportCards({ reports: { sales, agent, usage } }: { reports: Reports }) {
  const { funnel, attribution, handoff } = sales;
  const currency = attribution.direct.currency ?? attribution.assisted.currency ?? "TRY";
  return (
    <>
      <Card title="Satış hunisi">
        <Row gutter={16}>
          <Col xs={12} md={6}>
            <Statistic title="Widget açılışı (elle / proaktif)"
              value={`${funnel.widgetOpenedManual} / ${funnel.widgetOpenedProactive}`} />
          </Col>
          <Col xs={12} md={6}><Statistic title="Başlayan konuşma" value={funnel.conversationsStarted} /></Col>
          <Col xs={12} md={6}><Statistic title="Ürün önerilen" value={funnel.conversationsWithRecommendation} /></Col>
          <Col xs={12} md={6}><Statistic title="Sepete ekleyen" value={funnel.conversationsWithAddToCart} /></Col>
          <Col xs={12} md={6}>
            <Statistic title="Sepete ekleme (başarılı / istek)"
              value={`${funnel.addToCartSucceeded} / ${funnel.addToCartRequested}`} />
          </Col>
        </Row>
      </Card>

      <Card title="Dönüşüm (attribution)" extra={
        <Typography.Text type="secondary">Doğrudan: 24 saat ve önerilen ürün · Destekli: 7 gün</Typography.Text>}>
        <Row gutter={16}>
          <Col xs={12} md={6}><Statistic title="Doğrudan sipariş" value={attribution.direct.orders} /></Col>
          <Col xs={12} md={6}>
            <Statistic title="Doğrudan net ciro" value={money(attribution.direct.netMinor, currency)} />
            <Typography.Text type="secondary">Brüt {money(attribution.direct.grossMinor, currency)}</Typography.Text>
          </Col>
          <Col xs={12} md={6}>
            <Statistic title="Doğrudan dönüşüm oranı" value={percent(attribution.directConversionRate)} />
          </Col>
          <Col xs={12} md={6}>
            <Statistic title="Destekli sipariş" value={attribution.assisted.orders} />
            <Typography.Text type="secondary">Net {money(attribution.assisted.netMinor, currency)}</Typography.Text>
          </Col>
        </Row>
      </Card>

      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Card title="Satış Masası">
            <Row gutter={16}>
              <Col span={8}><Statistic title="İstenen" value={handoff.requested} /></Col>
              <Col span={8}><Statistic title="Devralınan" value={handoff.accepted} /></Col>
              <Col span={8}><Statistic title="Süresi dolan" value={handoff.timedOut} /></Col>
              <Col span={12}><Statistic title="Ortalama bekleme" value={seconds(handoff.averageWaitSeconds)} /></Col>
              <Col span={12}><Statistic title="Bekleme (p90)" value={seconds(handoff.p90WaitSeconds)} /></Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Asistan">
            <Row gutter={16}>
              <Col span={8}><Statistic title="Tur" value={agent.turns} /></Col>
              <Col span={8}><Statistic title="Gecikme p50" value={agent.p50DurationMs === null ? "—" : `${agent.p50DurationMs} ms`} /></Col>
              <Col span={8}><Statistic title="Gecikme p95" value={agent.p95DurationMs === null ? "—" : `${agent.p95DurationMs} ms`} /></Col>
              <Col span={8}><Statistic title="Hata" value={percent(agent.failedRate)} /></Col>
              <Col span={8}><Statistic title="Düşük işlev" value={percent(agent.degradedRate)} /></Col>
              <Col span={8}><Statistic title="Denetim değiştirme" value={percent(agent.guardReplacedRate)} /></Col>
              <Col span={8}><Statistic title="Önbellek isabeti" value={percent(agent.cacheHitRate)} /></Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Card title="En çok önerilen ürünler">
            <Table<SkuCount> rowKey="sku" size="small" pagination={false} dataSource={sales.topRecommended}
              locale={{ emptyText: "Bu dönemde öneri yok" }}
              columns={[{ title: "SKU", dataIndex: "sku" }, { title: "Öneri", dataIndex: "count", width: 100 }]} />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Sepete en çok eklenenler">
            <Table<SkuCount> rowKey="sku" size="small" pagination={false} dataSource={sales.topAddedToCart}
              locale={{ emptyText: "Bu dönemde sepete ekleme yok" }}
              columns={[{ title: "SKU", dataIndex: "sku" }, { title: "Ekleme", dataIndex: "count", width: 100 }]} />
          </Card>
        </Col>
      </Row>

      {agent.byModels.length > 0 && (
        <Card title="Model karşılaştırması" extra={
          <Typography.Text type="secondary">Yalnızca yapay zekâ kullanılan turlar</Typography.Text>}>
          <Table<AgentModelRow> rowKey="models" size="small" pagination={false} dataSource={agent.byModels}
            scroll={{ x: true }}
            columns={[
              { title: "Modeller", dataIndex: "models" },
              { title: "Tur", dataIndex: "turns" },
              { title: "Gecikme p95", dataIndex: "p95DurationMs", render: (v: number | null) => (v === null ? "—" : `${v} ms`) },
              { title: "Hata", dataIndex: "failedRate", render: percent },
              { title: "Denetim değiştirme", dataIndex: "guardReplacedRate", render: percent },
              { title: "Önbellek isabeti", dataIndex: "cacheHitRate", render: percent },
            ]} />
        </Card>
      )}

      <Card title="Kullanım" extra={!usage.providerCostComplete && (
        <Tag color="orange">Bazı modellerin fiyatı tanımlı değil; maliyet eksik</Tag>)}>
        {usage.budget && usage.budget.state !== "OK" && (
          <Alert style={{ marginBottom: 16 }} showIcon type={usage.budget.state === "EXCEEDED" ? "error" : "warning"}
            title={usage.budget.state === "EXCEEDED"
              ? `${usage.budget.month} ayı sağlayıcı bütçesi aşıldı`
              : `${usage.budget.month} ayı sağlayıcı bütçesinin ${percent(usage.budget.ratio)} kadarı kullanıldı`} />
        )}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={12} md={6}><Statistic title="Faturalanabilir konuşma" value={usage.billableConversations} /></Col>
          <Col xs={12} md={6}><Statistic title="Sağlayıcı maliyeti" value={usd(usage.providerCostMicros)} /></Col>
          <Col xs={12} md={6}>
            <Statistic title="Görüşme başına maliyet"
              value={usage.costPerConversationMicros === null ? "—" : usd(usage.costPerConversationMicros)} />
            {!usage.conversationCostComplete && <Typography.Text type="secondary">Eksik fiyat var</Typography.Text>}
          </Col>
          {usage.budget && (
            <Col xs={12} md={6}>
              <Statistic title={`Bu ayın bütçesi (${usage.budget.month})`}
                value={`${usd(usage.budget.spentMicros)} / ${usd(usage.budget.budgetMicros)}`} />
            </Col>
          )}
        </Row>
        <Table<UsageRow> rowKey={(r) => `${r.kind}:${r.provider ?? ""}`} size="small" pagination={false}
          dataSource={usage.rows} locale={{ emptyText: "Bu dönemde kullanım yok" }}
          columns={[
            { title: "Tür", dataIndex: "kind", render: (k: string) => KINDS[k] ?? k },
            { title: "Sağlayıcı / model", dataIndex: "provider", render: (p: string | null) => p ?? "—" },
            { title: "Miktar", dataIndex: "quantity", render: (q: number) => q.toLocaleString("tr-TR") },
            { title: "Maliyet", dataIndex: "providerCostMicros",
              render: (c: number | null, r) => (r.provider === null ? "—" : c === null ? "Fiyat yok" : usd(c)) },
          ]} />
      </Card>
    </>
  );
}
